import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { pageTitle } from "@/lib/brand";
import { useCustomerStore, type CustomerSettingsView } from "@/stores/customerStore";
import { saveSettings } from "@/data/firestore/settingsRepository";
import {
  editableSettings,
  mergeSettings,
  settingsChanges,
  settingsSchema,
  type EditableSettings,
} from "@/domain/settings";
import { useDashboardAccess } from "@/components/dashboard-access-context";
import { formatTaxRate, taxTypeKey } from "@/lib/tax";

export const Route = createFileRoute("/app/$customerId/settings")({
  head: () => ({
    meta: [
      { title: pageTitle("Settings") },
      {
        name: "description",
        content: "Business details, Discovery branding tokens, and notification preferences.",
      },
      { property: "og:title", content: pageTitle("Settings") },
      { property: "og:description", content: "Workspace, branding and notification preferences." },
    ],
  }),
  component: SettingsPage,
});

function SettingsPage() {
  const { customerId } = Route.useParams();
  const access = useDashboardAccess();
  return (
    <SettingsForm key={`${customerId}:${access.userId}:${access.mode}`} customerId={customerId} />
  );
}

export function SettingsForm({ customerId }: { customerId: string }) {
  const access = useDashboardAccess();
  const { t, tl, lang } = useI18n();
  const stored = useCustomerStore((state) => state.settings)!;
  const [form, setForm] = useState<{
    baseline: EditableSettings;
    edited: EditableSettings;
    savedSnapshot?: CustomerSettingsView;
  } | null>(null);
  // A successful save is authoritative while its subscription update is still in flight.
  const current = form?.savedSnapshot === stored ? form.baseline : editableSettings(stored);
  const [saving, setSaving] = useState(false);
  const values = form ? mergeSettings(current, form.baseline, form.edited) : current;
  const settings = { branding: values.branding, notifications: values.notifications };
  const business = { ...stored.profile, name: values.name, website: values.website };
  const editing = access.mode === "manage" && Boolean(access.userId) && !saving;
  const change = (
    update: (
      value: typeof settings & { profile: typeof business },
    ) => typeof settings & { profile: typeof business },
  ) => {
    const next = update({ ...settings, profile: business });
    setForm({
      ...(form?.savedSnapshot === stored ? { savedSnapshot: stored } : {}),
      baseline: form ? mergeSettings(current, form.edited, form.baseline) : current,
      edited: {
        name: next.profile.name,
        website: next.profile.website,
        branding: next.branding,
        notifications: next.notifications,
      },
    });
  };
  let dirty = false;
  let valid = true;
  try {
    dirty = Boolean(form && settingsChanges(form.baseline, form.edited).length);
  } catch {
    valid = false;
    dirty = true;
  }
  const leadEmailValid = settingsSchema.shape.notifications.shape.leadEmail.safeParse(
    values.notifications.leadEmail,
  ).success;
  const saveAll = async () => {
    if (!editing || !form || !dirty || !valid) return;
    setSaving(true);
    try {
      await saveSettings(customerId, form.baseline, form.edited);
      setForm({ baseline: values, edited: values, savedSnapshot: stored });
      toast.success(t("common.save"));
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : "Could not save workspace settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t("settings.title")}
        subtitle={t("settings.subtitle")}
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={!editing || !dirty}
              onClick={() =>
                setForm(form?.savedSnapshot === stored ? { ...form, edited: form.baseline } : null)
              }
            >
              {t("settings.discardChanges")}
            </Button>
            <Button onClick={() => void saveAll()} disabled={!editing || !dirty || !valid}>
              {saving ? "Saving..." : t("common.save")}
            </Button>
          </div>
        }
      />

      {!valid && (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {!leadEmailValid ? t("settings.invalidLeadEmail") : "Enter a valid website URL and settings before saving."}
        </p>
      )}
      <div className="grid gap-5 lg:grid-cols-2">
        <section className="surface-elevated animate-fade-up p-6">
          <h2 className="text-base font-extrabold">{t("settings.general")}</h2>
          <div className="mt-4 space-y-4">
            <Field
              disabled={!editing}
              label={t("settings.businessName")}
              value={tl(business.name)}
              onChange={(value) =>
                change((current) => ({
                  ...current,
                  profile: { ...current.profile, name: { ...current.profile.name, [lang]: value } },
                }))
              }
            />
            <Field
              disabled={!editing}
              label={t("settings.website")}
              value={business.website}
              onChange={(value) =>
                change((current) => ({
                  ...current,
                  profile: { ...current.profile, website: value },
                }))
              }
            />
            <Field disabled label={t("settings.currency")} value={business.currency} />
            <Field
              disabled
              label={t("settings.country")}
              value={business.countryCode
                ? new Intl.DisplayNames([lang], { type: "region" }).of(business.countryCode) ?? business.countryCode
                : t("settings.notSet")}
            />
            <Field disabled label={t("settings.taxType")} value={business.tax?.type ? t(taxTypeKey(business.tax.type)) : t("settings.notSet")} />
            <Field disabled label={t("settings.taxRate")} value={business.tax?.ratePercent == null ? t("settings.notSet") : formatTaxRate(business.tax.ratePercent, lang)} />
          </div>
        </section>

        <section className="brand-panel animate-fade-up p-6">
          <h2 className="text-base font-extrabold">{t("settings.branding")}</h2>
          <p className="mt-1 text-xs text-muted-foreground">{t("brand.tagline")}</p>
          <div className="mt-4 space-y-4">
            <div className="flex items-center gap-3">
              <span className="size-9 shrink-0 rounded-xl bg-primary" />
              <div className="min-w-0 flex-1">
                <Field
                  disabled={!editing}
                  label={t("settings.brandColor")}
                  value={settings.branding.primaryColor}
                  onChange={(value) =>
                    change((current) => ({
                      ...current,
                      branding: { ...current.branding, primaryColor: value },
                    }))
                  }
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="size-9 shrink-0 rounded-xl bg-teal" />
              <div className="min-w-0 flex-1">
                <Field
                  disabled={!editing}
                  label={t("settings.secondaryColor")}
                  value={settings.branding.secondaryColor}
                  onChange={(value) =>
                    change((current) => ({
                      ...current,
                      branding: { ...current.branding, secondaryColor: value },
                    }))
                  }
                />
              </div>
            </div>
            <Field
              disabled={!editing}
              label={t("settings.fontFamily")}
              value={settings.branding.fontFamily}
              onChange={(value) =>
                change((current) => ({
                  ...current,
                  branding: { ...current.branding, fontFamily: value },
                }))
              }
            />
            <Field
              disabled={!editing}
              label={t("settings.cornerRadius")}
              value={settings.branding.cornerRadius}
              onChange={(value) =>
                change((current) => ({
                  ...current,
                  branding: { ...current.branding, cornerRadius: value },
                }))
              }
            />
            <Field
              disabled={!editing}
              label={t("settings.buttonStyle")}
              value={settings.branding.buttonStyle}
              onChange={(value) =>
                change((current) => ({
                  ...current,
                  branding: { ...current.branding, buttonStyle: value },
                }))
              }
            />
            <Field
              disabled={!editing}
              label={t("settings.heroStyle")}
              value={settings.branding.heroStyle}
              onChange={(value) =>
                change((current) => ({
                  ...current,
                  branding: { ...current.branding, heroStyle: value },
                }))
              }
            />
          </div>
        </section>

        <section className="surface-elevated animate-fade-up p-6 lg:col-span-2">
          <h2 className="text-base font-extrabold">{t("settings.notifications")}</h2>
          <ul className="mt-4 divide-y divide-border">
            {[
              { key: "settings.emailAlerts", field: "emailAlerts" as const },
              { key: "settings.dailyDigest", field: "dailyDigest" as const },
              { key: "settings.handoffAlerts", field: "handoffAlerts" as const },
            ].map((row) => (
              <li
                key={row.key}
                className="app-row rounded-xl px-3 py-3.5"
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="min-w-0 text-sm font-medium">{t(row.key)}</span>
                  <Switch
                    disabled={!editing}
                    checked={settings.notifications[row.field]}
                    onCheckedChange={(checked) =>
                      change((current) => ({
                        ...current,
                        notifications: { ...current.notifications, [row.field]: checked },
                      }))
                    }
                    className="shrink-0"
                  />
                </div>
                {row.field === "emailAlerts" && (
                  <div className="mt-4 max-w-md">
                    <Field
                      disabled={!editing}
                      label={t("settings.leadEmail")}
                      type="email"
                      value={settings.notifications.leadEmail}
                      helper={t("settings.leadEmailDefault").replace("{contactEmail}", business.contact.email)}
                      invalid={!leadEmailValid}
                      onChange={(value) =>
                        change((current) => ({
                          ...current,
                          notifications: { ...current.notifications, leadEmail: value },
                        }))
                      }
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  disabled,
  type,
  helper,
  invalid,
}: {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  type?: "email";
  helper?: string;
  invalid?: boolean;
}) {
  return (
    <div>
      <Label htmlFor={label} className="text-xs font-semibold text-muted-foreground">
        {label}
      </Label>
      <Input
        id={label}
        type={type}
        aria-describedby={helper ? `${label}-helper` : undefined}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        className="mt-1.5"
      />
      {helper && <p id={`${label}-helper`} className="mt-1.5 text-xs text-muted-foreground">{helper}</p>}
    </div>
  );
}
